import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import adminApi from '../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const trend = (current, previous) => {
  if (previous == null || previous === 0 || current === previous) return 'flat';
  return current > previous ? 'up' : 'down';
};

const TrendPill = ({ current, previous }) => {
  const t = trend(current, previous);
  if (t === 'flat' || previous == null) return <span className="text-xs text-gray-400">—</span>;
  const isUp = t === 'up';
  const delta = current - previous;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${isUp ? 'text-emerald-600' : 'text-red-500'}`}>
      <i className={`fas fa-caret-${isUp ? 'up' : 'down'}`}></i>
      {Math.abs(delta).toFixed(2)}
    </span>
  );
};

const CurrencyRow = ({ c, onSave, onDelete }) => {
  const [rate, setRate] = useState(String(c.rate || c.sell || c.buy || ''));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setRate(String(c.rate || c.sell || c.buy || ''));
    setDirty(false);
  }, [c.rate, c.sell, c.buy]);

  const save = async () => {
    const r = parseFloat(rate);
    if (isNaN(r) || r <= 0) {
      toast.error('Enter a valid rate');
      return;
    }
    setSaving(true);
    try {
      await onSave(c.code, { rate: r });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!window.confirm(`Remove ${c.country} (${c.code}) from the ticker?`)) return;
    onDelete(c.code);
  };

  const currentRate = c.rate || c.sell || c.buy || 0;
  const prevRate = c.prev_rate != null ? c.prev_rate : (c.prev_sell != null ? c.prev_sell : c.prev_buy);

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all relative" data-testid={`currency-row-${c.code}`}>
      <button
        onClick={handleDelete}
        className="absolute top-2 right-2 w-7 h-7 rounded-full hover:bg-red-50 text-gray-300 hover:text-red-500 flex items-center justify-center transition-colors"
        title="Remove"
        data-testid={`delete-${c.code}-btn`}
      >
        <i className="fas fa-times text-sm"></i>
      </button>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-3xl leading-none">{c.flag}</span>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-800 truncate">{c.country}</p>
          <p className="text-xs text-gray-400 font-mono">EUR → {c.code}</p>
        </div>
      </div>

      <div className="mb-3">
        <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex items-center justify-between">
          <span className="flex items-center gap-1"><i className="fas fa-money-bill-wave text-orange-500 text-[10px]"></i> Rate</span>
          <TrendPill current={currentRate} previous={prevRate} />
        </label>
        <input
          type="number"
          step="0.01"
          value={rate}
          onChange={(e) => { setRate(e.target.value); setDirty(true); }}
          placeholder="0.00"
          className="w-full mt-1 px-3 py-2.5 border-2 border-gray-200 rounded-lg font-mono font-bold text-gray-800 focus:border-orange-500 focus:outline-none text-lg"
          data-testid={`rate-input-${c.code}`}
        />
        {prevRate != null && prevRate > 0 && (
          <p className="text-[10px] text-gray-400 mt-0.5">Prev: {Number(prevRate).toFixed(2)}</p>
        )}
      </div>

      <button
        onClick={save}
        disabled={saving || !dirty}
        className={`w-full py-2 rounded-lg text-sm font-bold transition-all ${
          dirty
            ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 shadow-md'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        data-testid={`save-${c.code}-btn`}
      >
        {saving ? <><i className="fas fa-spinner fa-spin mr-1"></i> Saving...</> : dirty ? <><i className="fas fa-save mr-1"></i> Save rate</> : 'Up to date'}
      </button>
    </div>
  );
};

const Currencies = () => {
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [newFlag, setNewFlag] = useState('');
  const [newRate, setNewRate] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/currencies`);
      setRates(res.data || []);
    } catch (err) {
      toast.error('Could not load currencies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRate = async (code, payload) => {
    try {
      const res = await adminApi.put(`/currencies/${code}`, payload);
      setRates((prev) => prev.map((r) => (r.code === code ? res.data : r)));
      toast.success(`${code} rates updated`);
    } catch (err) {
      toast.error(`Failed to update ${code}`);
      throw err;
    }
  };

  const deleteRate = async (code) => {
    try {
      await adminApi.delete(`/currencies/${code}`);
      setRates((prev) => prev.filter((r) => r.code !== code));
      toast.success(`${code} removed`);
    } catch (err) {
      toast.error(`Could not remove ${code}`);
    }
  };

  const addRate = async () => {
    const code = newCode.trim().toUpperCase();
    const country = newCountry.trim();
    if (!code || !country) { toast.error('Code and country are required'); return; }
    setAdding(true);
    try {
      const payload = {
        code,
        country,
        flag: newFlag.trim(),
        rate: parseFloat(newRate) || 0,
      };
      await adminApi.post('/currencies', payload);
      toast.success(`${code} added`);
      setNewCode(''); setNewCountry(''); setNewFlag(''); setNewRate('');
      setShowAddForm(false);
      await load(); // re-fetch so the new row appears in the correct sorted position
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not add currency';
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  // One-click presets — clicking a chip fills all 4 fields so admin only needs to enter the rate.
  const PRESETS = [
    { code: 'CAD', country: 'Canada',     flag: '🇨🇦' },
    { code: 'AUD', country: 'Australia',  flag: '🇦🇺' },
    { code: 'JPY', country: 'Japan',      flag: '🇯🇵' },
    { code: 'CNY', country: 'China',      flag: '🇨🇳' },
    { code: 'AED', country: 'UAE',        flag: '🇦🇪' },
    { code: 'SAR', country: 'Saudi Arabia', flag: '🇸🇦' },
    { code: 'THB', country: 'Thailand',   flag: '🇹🇭' },
    { code: 'RUB', country: 'Russia',     flag: '🇷🇺' },
    { code: 'MXN', country: 'Mexico',     flag: '🇲🇽' },
    { code: 'IDR', country: 'Indonesia',  flag: '🇮🇩' },
    { code: 'KES', country: 'Kenya',      flag: '🇰🇪' },
    { code: 'ZAR', country: 'South Africa', flag: '🇿🇦' },
  ];
  const applyPreset = (p) => {
    setNewCode(p.code);
    setNewCountry(p.country);
    setNewFlag(p.flag);
    setShowAddForm(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <button
            onClick={() => navigate('/staff')}
            className="inline-flex items-center space-x-2 px-5 py-3 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-md border border-gray-200"
            data-testid="back-to-admin-btn"
          >
            <i className="fas fa-arrow-left"></i>
            <span>Back to Admin</span>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddForm((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 shadow-md"
              data-testid="toggle-add-btn"
            >
              <i className={`fas ${showAddForm ? 'fa-minus' : 'fa-plus'}`}></i>
              {showAddForm ? 'Cancel' : 'Add currency'}
            </button>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 shadow-md"
              data-testid="refresh-btn"
            >
              <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i> Refresh
            </button>
          </div>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <i className="fas fa-money-bill-wave text-orange-500"></i>
            Currency Rates
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            Set the send rate for each currency. Changes appear instantly on the home page ticker,
            with an up/down arrow based on the previous value.
          </p>
        </div>

        {/* Add Currency Form */}
        {showAddForm && (
          <div className="bg-white rounded-2xl shadow-lg border-2 border-emerald-200 p-5 mb-6" data-testid="add-currency-form">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <i className="fas fa-plus-circle text-emerald-600"></i> Add a new currency
            </h3>

            {/* One-click presets */}
            <div className="mb-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Quick pick a country</p>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p.code}
                    onClick={() => applyPreset(p)}
                    className="px-2.5 py-1.5 bg-slate-100 hover:bg-emerald-100 hover:border-emerald-300 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 transition-all flex items-center gap-1.5"
                    data-testid={`preset-${p.code}`}
                    type="button"
                  >
                    <span className="text-base leading-none">{p.flag}</span>
                    <span>{p.country}</span>
                    <span className="text-slate-400 font-mono text-[10px]">{p.code}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Code *</label>
                <input type="text" value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="USD" maxLength={5} className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg font-mono font-bold uppercase focus:border-orange-500 focus:outline-none" data-testid="new-code-input" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Country *</label>
                <input type="text" value={newCountry} onChange={(e) => setNewCountry(e.target.value)} placeholder="United States" className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none" data-testid="new-country-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Flag emoji</label>
                <input type="text" value={newFlag} onChange={(e) => setNewFlag(e.target.value)} placeholder="🇺🇸" maxLength={4} className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-2xl focus:border-orange-500 focus:outline-none" data-testid="new-flag-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Rate (optional)</label>
                <input type="number" step="0.01" value={newRate} onChange={(e) => setNewRate(e.target.value)} placeholder="0.00" className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg font-mono focus:border-orange-500 focus:outline-none" data-testid="new-rate-input" />
              </div>
              <div className="flex items-end col-span-2 md:col-span-1">
                <button onClick={addRate} disabled={adding} className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg disabled:opacity-50" data-testid="submit-add-btn">
                  {adding ? <><i className="fas fa-spinner fa-spin"></i></> : <><i className="fas fa-plus mr-1"></i> Add</>}
                </button>
              </div>
              <p className="col-span-2 md:col-span-5 text-[11px] text-gray-400">
                Tip: Paste the flag emoji directly. You can find them at emojipedia.org.
              </p>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6 flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2"><i className="fas fa-caret-up text-emerald-600"></i> <span className="text-gray-600">Rate went up</span></div>
          <div className="flex items-center gap-2"><i className="fas fa-caret-down text-red-500"></i> <span className="text-gray-600">Rate went down</span></div>
          <div className="flex items-center gap-2"><span className="text-gray-400">—</span> <span className="text-gray-600">No previous value</span></div>
          <div className="ml-auto text-xs text-gray-400">
            <i className="fas fa-info-circle mr-1"></i>
            Rates are per 1 EUR. Rates persist until you change them.
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-12 text-center text-gray-400">
            <i className="fas fa-spinner fa-spin text-3xl mb-3"></i>
            <p>Loading rates...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="currencies-grid">
            {rates.map((c) => (
              <CurrencyRow key={c.code} c={c} onSave={saveRate} onDelete={deleteRate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Currencies;
